// =============================================================================
// VandieZ agent-web -- Reports (/reports)
// =============================================================================
// FSD p62 + p69. Date filters, the four required analyses (route, driver,
// goods type, cancellation) and status counts at a glance.
//
// THIS PAGE EXISTS BECAUSE THE ENDPOINT DID NOT HAVE ONE. `GET
// /broker/reports/analysis` shipped earlier the same day, tested and
// migrated, and no client called it — the exact defect documented in
// `docs/superpowers/findings/2026-08-06-write-only-values.md`, committed by
// the same author on the same day. Found by re-running the headless sweep.
//
// `total_value_inr` IS NULLABLE AND THAT IS MEANINGFUL. Null means nothing
// in the bucket completed, which is not the same as earning zero: a lane
// with three cancelled loads and no completions has undefined value, not ₹0.
// Rendering it as ₹0 would tell an agent a lane is unprofitable when what it
// actually is, is untested.
// =============================================================================

import { AlertCircle, Download } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { brokerApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  AgentAnalysisResponse,
  AgentBreakdownRow,
} from "@/types/broker";

/** `YYYY-MM-DD` in LOCAL time.
 *
 * Not `toISOString().slice(0,10)`, which converts to UTC first: for a
 * user in IST that reports yesterday for the first 5.5 hours of every
 * day, so "this month" would start a day early each morning.
 */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface Period {
  key: string;
  label: string;
  from: string;
  to: string;
}

/** p69: "this month, last month, custom date range". */
function periods(now: Date = new Date()): [Period, Period, Period] {
  const som = new Date(now.getFullYear(), now.getMonth(), 1);
  const solm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const eolm = new Date(now.getFullYear(), now.getMonth(), 0);
  const ninety = new Date(now);
  ninety.setDate(ninety.getDate() - 89);
  return [
    { key: "this_month", label: "This month", from: isoDate(som), to: isoDate(now) },
    { key: "last_month", label: "Last month", from: isoDate(solm), to: isoDate(eolm) },
    { key: "last_90", label: "Last 90 days", from: isoDate(ninety), to: isoDate(now) },
  ];
}

function formatINR(amount: string | null): string {
  if (amount === null) {
    // See the header note: undefined, not zero.
    return "—";
  }
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {
    return amount;
  }
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** p69: "include export options for web dashboard". One file with a
 *  section per analysis, because four separate downloads for one report
 *  is four things to reassemble in a spreadsheet. */
function downloadCsv(a: AgentAnalysisResponse): void {
  const lines: string[] = [];
  const sections: [string, AgentBreakdownRow[]][] = [
    ["Route", a.by_route],
    ["Driver", a.by_driver],
    ["Goods type", a.by_goods_type],
    ["Cancellation reason", a.cancellations],
  ];
  lines.push(`VandieZ agent report,${a.from_date} to ${a.to_date}`);
  lines.push("");
  for (const [title, rows] of sections) {
    lines.push(title);
    lines.push("key,loads,completed,cancelled,value_inr");
    for (const r of rows) {
      lines.push(
        [
          r.key,
          String(r.load_count),
          String(r.completed_count),
          String(r.cancelled_count),
          r.total_value_inr ?? "",
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    lines.push("");
  }
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const el = document.createElement("a");
  el.href = url;
  el.download = `vandiez-agent-report-${a.from_date}-to-${a.to_date}.csv`;
  el.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function Breakdown({
  title,
  description,
  rows,
  keyHeading,
}: {
  title: string;
  description: string;
  rows: AgentBreakdownRow[];
  keyHeading: string;
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Nothing in this period.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-2 pr-4">{keyHeading}</th>
                  <th className="py-2 pr-4">Loads</th>
                  <th className="py-2 pr-4">Completed</th>
                  <th className="py-2 pr-4">Cancelled</th>
                  <th className="py-2 pr-4">Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{r.key}</td>
                    <td className="py-2 pr-4">{r.load_count}</td>
                    <td className="py-2 pr-4">{r.completed_count}</td>
                    <td className="py-2 pr-4">{r.cancelled_count}</td>
                    <td className="py-2 pr-4">{formatINR(r.total_value_inr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ReportsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const all = periods();
  const [periodKey, setPeriodKey] = useState<string>(all[0].key);
  const [data, setData] = useState<AgentAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const period = all.find((p) => p.key === periodKey) ?? all[0];

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await brokerApi.analysis(
          accessToken,
          { from: period.from, to: period.to },
          signal,
        );
        setData(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(
          e instanceof ApiError ? e.problem.detail : "Could not load reports.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, period.from, period.to],
  );

  useEffect(() => {
    const c = new AbortController();
    void load(c.signal);
    return () => {
      c.abort();
    };
  }, [load]);

  const counts = data?.status_counts;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Reports" showOwnerSwitcher={false} />
      <main className="mx-auto max-w-5xl space-y-4 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {all.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  setPeriodKey(p.key);
                }}
                className={
                  p.key === periodKey
                    ? "rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                    : "rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/80"
                }
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={data === null}
            onClick={() => {
              if (data !== null) {
                downloadCsv(data);
              }
            }}
          >
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>

        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* p69: "active, completed, cancelled and postponed at a glance" */}
        <div className="grid gap-4 sm:grid-cols-4">
          {[
            ["Active", counts?.active],
            ["Completed", counts?.completed],
            ["Cancelled", counts?.cancelled],
            ["Postponed", counts?.postponed],
          ].map(([label, value]) => (
            <Card key={label as string}>
              <CardHeader className="pb-2">
                <CardDescription>{label as string}</CardDescription>
                <CardTitle className="text-2xl">
                  {loading || value === undefined ? "—" : value}
                </CardTitle>
              </CardHeader>
            </Card>
          ))}
        </div>

        <Breakdown
          title="By route"
          description="Which lanes carry the volume, and which of them actually complete."
          keyHeading="Lane"
          rows={data?.by_route ?? []}
        />
        <Breakdown
          title="By driver"
          description="Volume and completion per driver."
          keyHeading="Driver"
          rows={data?.by_driver ?? []}
        />
        <Breakdown
          title="By goods type"
          description="What you actually move."
          keyHeading="Goods"
          rows={data?.by_goods_type ?? []}
        />
        <Breakdown
          title="Cancellations"
          description="Bucketed by the reason given. A large '(no reason given)' bucket is itself a finding — it means the cancel flow is not asking properly."
          keyHeading="Reason"
          rows={data?.cancellations ?? []}
        />
      </main>
    </div>
  );
}
